import React, { useState } from "react";
import {
  Avatar,
  Badge,
  Button,
  Card,
  Divider,
  Grid,
  Group,
  SimpleGrid,
  Stack,
  Text,
  Title,
  FileInput,
  Modal,
  Image,
  Accordion,
  Paper,
  ThemeIcon,
} from "@mantine/core";
import { FiMail, FiMapPin, FiGlobe, FiUpload, FiEdit3 } from "react-icons/fi";
import css2 from "./../../../Insights/pages/MeetingTime/AsAttendee/AsAttendee.module.scss";
import { useOutletContext } from "react-router-dom";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { doc, updateDoc } from "firebase/firestore";
import { FirestoreDB, StorageDB } from "utils/firebase";
import { showNotification } from "@mantine/notifications";

const GeneralInfo = () => {
  const [orgDetails] = useOutletContext();
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const handleFileSelect = (file) => {
    if (file) {
      setSelectedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleLogoUpload = async () => {
    if (!selectedFile || !orgDetails?.id) return;

    setUploading(true);
    try {
      // Upload to Firebase Storage
      const storageRef = ref(
        StorageDB,
        `organizations/${orgDetails.id}/logo/${selectedFile.name}`
      );
      const snapshot = await uploadBytes(storageRef, selectedFile);
      const downloadURL = await getDownloadURL(snapshot.ref);

      // Update Firestore document
      const orgRef = doc(FirestoreDB, "organizations", orgDetails.id);
      await updateDoc(orgRef, {
        photoUrl: downloadURL,
      });

      showNotification({
        title: "Success",
        message: "Organization logo updated successfully!",
        color: "green",
      });

      // Close modal and reset state
      setShowUploadModal(false);
      setSelectedFile(null);
      setPreviewUrl(null);

      // Force refresh of orgDetails (you might need to implement this based on your data fetching strategy)
      window.location.reload();
    } catch (error) {
      console.error("Error uploading logo:", error);
      showNotification({
        title: "Error",
        message: "Failed to update organization logo. Please try again.",
        color: "red",
      });
    } finally {
      setUploading(false);
    }
  };

  const openUploadModal = () => {
    setShowUploadModal(true);
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  return (
    <div className={css2.container}>
      <Stack spacing="lg" w={"100%"} py={30}>
        <Grid gutter="lg">
          <Grid.Col md={8} sm={12}>
            <Card withBorder radius="md" p="lg">
              <Group position="apart" mb="xs">
                <Title order={4}>Organization Details</Title>
                {orgDetails?.status && (
                  <Badge size="sm" variant="light" color="teal">
                    {orgDetails.status}
                  </Badge>
                )}
              </Group>
              <Text size="sm" color="dimmed">
                Executive overview of your organization profile
              </Text>
              <Divider my="md" />

              <SimpleGrid
                cols={2}
                spacing="md"
                breakpoints={[{ maxWidth: "sm", cols: 1 }]}
              >
                <Stack spacing={4}>
                  <Text size="xs" color="dimmed">
                    Organization Name
                  </Text>
                  <Text weight={500} tt={"capitalize"}>
                    {orgDetails?.legal_name}
                  </Text>
                </Stack>
                <Stack spacing={4}>
                  <Text size="xs" color="dimmed">
                    Primary Email
                  </Text>
                  <Group spacing="xs">
                    <ThemeIcon color="blue" variant="light" size="sm">
                      <FiMail />
                    </ThemeIcon>
                    <Text weight={500}>{orgDetails?.primary_email}</Text>
                  </Group>
                </Stack>
              </SimpleGrid>

              <Divider my="md" />
              <Text size="sm" color="dimmed" mb={8}>
                Departments
              </Text>
              {orgDetails?.departments?.length ? (
                <Group spacing={8}>
                  {orgDetails?.departments?.map((dep) => (
                    <Badge key={dep} radius="sm" variant="light" color="gray">
                      {dep?.name}
                    </Badge>
                  ))}
                </Group>
              ) : (
                <Text size="sm" color="dimmed">
                  No departments added
                </Text>
              )}
            </Card>

            <Card withBorder radius="md" p="lg" mt={20}>
              <Title order={4}>Locations</Title>
              <Divider my="md" />
              {orgDetails?.locations?.length ? (
                <Accordion variant="separated" radius="md">
                  {orgDetails.locations.map((location, index) => (
                    <Accordion.Item
                      key={`${location.city}-${index}`}
                      value={`loc-${index}`}
                    >
                      <Accordion.Control
                        icon={
                          <ThemeIcon color="blue" variant="light">
                            <FiMapPin />
                          </ThemeIcon>
                        }
                      >
                        <Group position="apart" w="100%">
                          <Text weight={500} size="sm">
                            {location.city}, {location.state}
                          </Text>
                          <Badge size="xs" variant="light" color="blue">
                            Location {index + 1}
                          </Badge>
                        </Group>
                      </Accordion.Control>
                      <Accordion.Panel>
                        <Stack spacing={10}>
                          <Stack spacing={4}>
                            <Text size="xs" color="dimmed">
                              Address Line 1
                            </Text>
                            <Text weight={500} size="sm">
                              {location.line1}
                            </Text>
                          </Stack>
                          {location.line2 && (
                            <Stack spacing={4}>
                              <Text size="xs" color="dimmed">
                                Address Line 2
                              </Text>
                              <Text weight={500} size="sm">
                                {location.line2}
                              </Text>
                            </Stack>
                          )}
                          <SimpleGrid cols={2} spacing="md">
                            <Stack spacing={4}>
                              <Text size="xs" color="dimmed">
                                City
                              </Text>
                              <Text weight={500} size="sm">
                                {location.city}
                              </Text>
                            </Stack>
                            <Stack spacing={4}>
                              <Text size="xs" color="dimmed">
                                State
                              </Text>
                              <Text weight={500} size="sm">
                                {location.state}
                              </Text>
                            </Stack>
                          </SimpleGrid>
                          <SimpleGrid cols={2} spacing="md">
                            <Stack spacing={4}>
                              <Text size="xs" color="dimmed">
                                Country
                              </Text>
                              <Group spacing="xs">
                                <FiGlobe />
                                <Text weight={500} size="sm">
                                  {location.country}
                                </Text>
                              </Group>
                            </Stack>
                            <Stack spacing={4}>
                              <Text size="xs" color="dimmed">
                                Postal Code
                              </Text>
                              <Text weight={500} size="sm">
                                {location.postal_code}
                              </Text>
                            </Stack>
                          </SimpleGrid>
                        </Stack>
                      </Accordion.Panel>
                    </Accordion.Item>
                  ))}
                </Accordion>
              ) : (
                <Text size="sm" color="dimmed">
                  No locations added
                </Text>
              )}
            </Card>
          </Grid.Col>

          <Grid.Col md={4} sm={12}>
            <Card withBorder radius="md" p="lg">
              <Title order={4}>Organization Logo</Title>
              <Divider my="md" />
              <Stack spacing="sm" align="center">
                <Paper withBorder radius="xl" p={6}>
                  <Avatar
                    radius="xl"
                    size={120}
                    src={orgDetails?.photoUrl}
                    alt={orgDetails?.legal_name}
                  >
                    {orgDetails?.legal_name?.[0]}
                  </Avatar>
                </Paper>
                <Text size="xs" color="dimmed">
                  PNG or SVG, 512x512 recommended
                </Text>
                <Button
                  leftIcon={<FiEdit3 />}
                  variant="gradient"
                  gradient={{ from: "blue", to: "indigo" }}
                  size="sm"
                  onClick={openUploadModal}
                  fullWidth
                >
                  Update Logo
                </Button>
              </Stack>
            </Card>
          </Grid.Col>
        </Grid>

      </Stack>

      {/* Logo Upload Modal */}
      <Modal
        opened={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        title="Update Organization Logo"
        size="md"
      >
        <Stack spacing="md">
          <FileInput
            label="Select Logo Image"
            placeholder="Choose an image file"
            accept="image/*"
            value={selectedFile}
            onChange={handleFileSelect}
            leftSection={<FiUpload />}
            required
          />

          {previewUrl && (
            <Stack spacing="xs" align="center">
              <Text size="sm" weight={500}>
                Preview:
              </Text>
              <Image
                src={previewUrl}
                alt="Logo preview"
                radius="md"
                width={250}
                height={250}
                style={{ objectFit: "contain" }}
              />
            </Stack>
          )}

          <Group position="right" mt="md">
            <Button
              variant="outline"
              onClick={() => setShowUploadModal(false)}
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleLogoUpload}
              loading={uploading}
              disabled={!selectedFile}
              leftIcon={<FiUpload />}
            >
              {uploading ? "Uploading..." : "Upload Logo"}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
};

export default GeneralInfo;
